import {
  ELEMENT_CATEGORIES,
  ElementCategory,
  type Applicability,
  type AssumptionKey,
  type AssumptionSource,
  type BasisStatus,
  type ConfidenceBand,
  type EffectiveApplicability,
  type EvidenceStatus,
  type ExecutionMethod,
  type InterpretationKey,
  type InterpretationSource,
  type Necessity,
  type ParticipantLink,
  type ParticipantRole,
  type PlanCategory,
  type PlanOrigin,
  type PlanRequirementType,
  type PlanReviewStatus,
  type ProductionDepartment,
  type ProductionEventType,
  type QuantitySource,
  type ResourceType,
  type RiskFlag,
  type SourcingStatus,
  type StateKind,
  type VersionComparison,
} from '@scoutaround/script-breakdown-shared';

import type { WorkState } from '../ui/StatusPill.js';

/**
 * THE PRODUCTION PLAN'S WORDS, in one place.
 *
 * The contract's enums are contracts (`shared/src/production-plan.ts`); what a
 * producer READS is here. Two rules shape every entry:
 *
 * - an interpretation is never worded as a fact, and an origin always says
 *   whether the screenplay states the thing or the plan reasoned it;
 * - the workspace vocabulary holds (`vocabulary.test.ts`): no "proposed",
 *   "dismissed" or "sourcing" on screen, so finding a resource is "searching"
 *   or "finding", never the term of art.
 */

export const ORIGIN_LABELS: Readonly<Record<PlanOrigin, string>> = {
  EXPLICIT: 'Explicit',
  DERIVED: 'Derived',
  SUGGESTED: 'Suggested',
  MANUAL: 'Manual',
};

/** What each origin means, said once beside the word. */
export const ORIGIN_HINTS: Readonly<Record<PlanOrigin, string>> = {
  EXPLICIT: 'The screenplay states it.',
  DERIVED: 'The plan reasoned it from what the screenplay states.',
  SUGGESTED: 'Worth considering; nothing in the screenplay requires it.',
  MANUAL: 'Added by a person on this production.',
};

export const NECESSITY_LABELS: Readonly<Record<Necessity, string>> = {
  REQUIRED: 'Required',
  CONDITIONAL: 'Conditional',
  OPTIONAL: 'Optional',
};

/** Effective applicability, once the chosen methods are known — said in words. */
export const APPLICABILITY_LABELS: Readonly<Record<EffectiveApplicability, string>> = {
  APPLIES: 'Applies',
  PENDING_METHOD: 'Depends on the method',
  POSSIBLE: 'Possible',
  NOT_APPLICABLE: 'Not needed with the chosen method',
};

/** A requirement's place in one method's dependency matrix. */
export const OPTION_APPLICABILITY_LABELS: Readonly<Record<Applicability, string>> = {
  REQUIRED_IF_SELECTED: 'Needed with this method',
  POTENTIALLY_REQUIRED: 'May be needed',
  OPTIONAL: 'Optional',
  NOT_APPLICABLE: 'Not needed',
};

export const REVIEW_STATE: Readonly<Record<PlanReviewStatus, { state: WorkState; label?: string }>> = {
  PENDING: { state: 'READY_FOR_REVIEW', label: 'Awaiting your decision' },
  ACCEPTED: { state: 'CONFIRMED', label: 'Accepted' },
  REJECTED: { state: 'EXCLUDED', label: 'Rejected' },
  NEEDS_REVALIDATION: { state: 'OUTDATED', label: 'Changed since reviewed' },
};

export const REVIEW_FILTER_LABELS: Readonly<Record<PlanReviewStatus, string>> = {
  PENDING: 'Awaiting decision',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  NEEDS_REVALIDATION: 'Changed',
};

export const BAND_LABELS: Readonly<Record<ConfidenceBand, string>> = {
  HIGH: 'High confidence',
  NEEDS_REVIEW: 'Needs a check',
  AMBIGUOUS: 'Ambiguous',
};

export const BASIS_LABELS: Readonly<Record<BasisStatus, string>> = {
  VALID: 'What it rests on still holds.',
  CHANGED: 'What it rests on has changed since it was derived.',
  NOT_REPRODUCED: 'The latest reading did not produce it again.',
  MANUAL: 'Added by hand: no rule behind it.',
};

export const QUANTITY_SOURCE_LABELS: Readonly<Record<QuantitySource, string>> = {
  EXPLICIT: 'stated in the screenplay',
  DERIVED_MINIMUM: 'a reasoned minimum',
  ESTIMATE: 'an estimate',
  UNKNOWN: 'not stated',
};

export const COMPARISON_LABELS: Readonly<Record<VersionComparison, string>> = {
  NEW: 'New in this draft',
  UNCHANGED: 'Unchanged since the last draft',
  CHANGED: 'Changed since the last draft',
  REMOVED: 'Removed in this draft',
  UNCERTAIN_MATCH: 'May match an earlier requirement',
};

export const METHOD_LABELS: Readonly<Record<ExecutionMethod, string>> = {
  PRACTICAL: 'Practical',
  DIGITAL: 'Digital',
  HYBRID: 'Hybrid',
  OTHER: 'Other',
};

export const METHODS: readonly ExecutionMethod[] = ['PRACTICAL', 'DIGITAL', 'HYBRID', 'OTHER'];

export const SOURCING_LABELS: Readonly<Record<SourcingStatus, string>> = {
  NOT_SEARCHED: 'Not searched',
  MATCH_CANDIDATES_FOUND: 'Match candidates found',
  NO_MATCH: 'No match',
  EXTERNAL_SOURCING: 'Being found outside ScoutAround',
  CONFIRMED: 'Confirmed',
};

export const DEPARTMENT_LABELS: Readonly<Record<ProductionDepartment, string>> = {
  ASSISTANT_DIRECTORS: 'Assistant directors',
  PRODUCTION_OFFICE: 'Production office',
  CASTING: 'Casting',
  STUNTS: 'Stunts',
  SPECIAL_EFFECTS: 'Special effects',
  VISUAL_EFFECTS: 'Visual effects',
  ART: 'Art',
  GRAPHICS_PLAYBACK: 'Graphics & playback',
  CONSTRUCTION: 'Construction',
  COSTUME: 'Costume',
  HAIR_MAKEUP: 'Hair & make-up',
  PROSTHETICS: 'Prosthetics',
  TRANSPORT: 'Transport',
  ANIMALS: 'Animals',
  ARMOURY: 'Armoury',
  CAMERA: 'Camera',
  GRIP_ELECTRIC: 'Grip & electric',
  SOUND: 'Sound',
  MUSIC_PLAYBACK: 'Music playback',
  LOCATIONS: 'Locations',
  HEALTH_SAFETY: 'Health & safety',
  SECURITY: 'Security',
  INTIMACY_COORDINATION: 'Intimacy coordination',
  CHILD_WELFARE: 'Child welfare',
  POST_PRODUCTION: 'Post-production',
};

export const DEPARTMENTS = Object.keys(DEPARTMENT_LABELS) as ProductionDepartment[];

export const RESOURCE_LABELS: Readonly<Record<ResourceType, string>> = {
  LOCATION: 'Locations',
  PERFORMER: 'Performers',
  SUPPORTING_ARTIST: 'Supporting artists',
  SPECIALIST_CREW: 'Specialist crew',
  PHYSICAL_ASSET: 'Physical assets',
  VEHICLE: 'Vehicles',
  ANIMAL: 'Animals',
  EQUIPMENT: 'Equipment',
  PRACTICAL_EFFECT: 'Practical effects',
  DIGITAL_EFFECT: 'Digital effects',
  DIGITAL_ASSET: 'Digital assets',
  SET_BUILD: 'Set builds',
  PERMIT_ACCESS: 'Permits & access',
  SERVICE: 'Services',
};

export const RESOURCE_TYPES = Object.keys(RESOURCE_LABELS) as ResourceType[];

export const REQUIREMENT_TYPE_LABELS: Readonly<Record<PlanRequirementType, string>> = {
  ASSET: 'Asset',
  PERFORMANCE: 'Performance',
  STUNT_SEQUENCE: 'Stunt sequence',
  EFFECT: 'Effect',
  SPECIALIST: 'Specialist',
  EQUIPMENT: 'Equipment',
  LOCATION_NEED: 'Location need',
  PERMIT_ACCESS: 'Permit or access',
  SAFETY_REVIEW: 'Safety review',
  COMPLIANCE_REVIEW: 'Compliance review',
  CONTINUITY: 'Continuity',
  FACT_REVIEW: 'Fact check',
  SERVICE: 'Service',
};

export const REQUIREMENT_TYPES = Object.keys(REQUIREMENT_TYPE_LABELS) as PlanRequirementType[];

export const RISK_LABELS: Readonly<Record<RiskFlag, string>> = {
  STUNT: 'Stunt',
  FIRE: 'Fire',
  EXPLOSIVE: 'Explosive',
  WEAPON: 'Weapon',
  WATER: 'Water',
  HEIGHT: 'Height',
  VEHICLE: 'Vehicle',
  MINOR: 'Minor',
  ANIMAL: 'Animal',
  INTIMACY: 'Intimacy',
  CROWD: 'Crowd',
  PUBLIC_ACCESS: 'Public access',
  HAZARDOUS_MATERIAL: 'Hazardous material',
  NIGHT_WORK: 'Night work',
  RIGGING: 'Rigging',
};

export const EVENT_TYPE_LABELS: Readonly<Record<ProductionEventType, string>> = {
  VEHICLE_ACTION: 'Vehicle action',
  DRIVING: 'Driving',
  FIGHT: 'Fight',
  FALL: 'Fall',
  HEIGHT: 'Height',
  FIRE: 'Fire',
  EXPLOSION: 'Explosion',
  DESTRUCTION: 'Destruction',
  GUNFIRE: 'Gunfire',
  WATER: 'Water',
  WEATHER: 'Weather',
  CREATURE: 'Creature',
  TRANSFORMATION: 'Transformation',
  SCREEN_CONTENT: 'Screen content',
  OFF_SCREEN_SOUND: 'Off-screen sound',
  REMOTE_VOICE: 'Remote voice',
  INTIMACY: 'Intimacy',
  CROWD_ACTION: 'Crowd action',
  ANIMAL_ACTION: 'Animal action',
  MACHINERY_ACTION: 'Machinery',
  FOOD_DRINK: 'Food & drink',
  PLAYBACK: 'Playback',
  DEVICE_IN_USE: 'Device in use',
  STATE_CHANGE: 'State change',
};

export const ROLE_LABELS: Readonly<Record<ParticipantRole, string>> = {
  PERFORMER: 'Performer',
  GROUP: 'Group',
  STORY_OBJECT: 'Object',
  VEHICLE: 'Vehicle',
  ANIMAL: 'Animal',
  CREATURE: 'Creature',
  DEVICE: 'Device',
  STRUCTURE: 'Structure',
};

export const LINK_LABELS: Readonly<Record<ParticipantLink, string>> = {
  LINKED: 'On the breakdown',
  AMBIGUOUS: 'Link unclear',
  STORY_ENTITY: 'In the story only',
};

export const STATE_LABELS: Readonly<Record<StateKind, string>> = {
  DAMAGED: 'damaged',
  DESTROYED: 'destroyed',
  TORN: 'torn',
  WET: 'wet',
  BLOODIED: 'bloodied',
  DIRTY: 'dirty',
  BURNED: 'burned',
  INJURED: 'injured',
  AGED: 'aged',
  CHANGED_APPEARANCE: 'changed in appearance',
  CHANGED_OWNER: 'changes hands',
  REPAIRED: 'repaired',
  CLEANED: 'cleaned',
  HEALED: 'healed',
  SWITCHED_ON: 'switched on',
  SWITCHED_OFF: 'switched off',
  OTHER: 'changed',
};

export const EVIDENCE_STATUS_LABELS: Readonly<Record<EvidenceStatus, string | null>> = {
  VERIFIED: null,
  RELOCATED: 'found elsewhere in the scene',
  AMBIGUOUS_MATCH: 'appears more than once',
};

export const ASSUMPTION_LABELS: Readonly<Record<AssumptionKey, string>> = {
  TERRITORY: 'Territory',
  PRODUCTION_MODEL: 'Production model',
  FORMAT: 'Format',
  BUDGET_BAND: 'Budget band',
  STUDIO_PREFERENCE: 'Studio preference',
  INTERIOR_EXTERIOR: 'Interior or exterior',
  SHOOT_WINDOW: 'Shoot window',
  CONSTRAINTS_NOTE: 'Constraints',
  EXECUTION_PREFERENCE: 'Execution preference',
};

export const ASSUMPTION_SOURCE_LABELS: Readonly<Record<AssumptionSource, string>> = {
  EVENT_OVERRIDE: 'set for this event',
  SCENE_OVERRIDE: 'set for this scene',
  PROJECT_FIELD: "from the production's details",
  NOT_SET: 'not set',
};

const INTERPRETATION_KEY_LABELS: Readonly<Record<InterpretationKey, string>> = {
  HAZARD: 'hazard',
  SCALE: 'scale',
  INVOLVES_MINOR_CHARACTER: 'involves a minor character',
  HEARD_ONLY: 'heard only',
  PRESENCE: 'presence',
};

export const INTERPRETATION_SOURCE_LABELS: Readonly<Record<InterpretationSource, string>> = {
  MODEL: 'AI',
  RULE: 'rule',
};

/** An enum value in plain words: `HAZARDOUS` → "hazardous", `OFF_SCREEN` → "off screen". */
export function words(value: string): string {
  return value.toLowerCase().replace(/_/g, ' ');
}

/**
 * An interpretation in words, never as a fact: the HAZARD key reads as its value
 * alone ("hazardous"); a yes/no key reads as the key itself or its negation.
 */
export function interpretationText(key: InterpretationKey, value: string): string {
  if (key === 'HAZARD') return words(value);
  if (value === 'true') return INTERPRETATION_KEY_LABELS[key];
  if (value === 'false') return `not ${INTERPRETATION_KEY_LABELS[key]}`;

  return `${INTERPRETATION_KEY_LABELS[key]}: ${words(value)}`;
}

export function categoryLabel(category: PlanCategory | null): string {
  if (category === null) return 'No category';
  if (category === 'LOCATION') return 'Location';

  return ELEMENT_CATEGORIES[category].label;
}

/** Every category in the sheet's order, LOCATION last. */
export const PLAN_CATEGORIES: readonly PlanCategory[] = [...ElementCategory.options, 'LOCATION'];
